/**
 * CAT Form Component (Multi-Step Wizard)
 *
 * Form for creating and editing CAT records with step-by-step navigation.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Autocomplete,
  SelectChangeEvent,
  Stepper,
  Step,
  StepLabel,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { CompanySelectionModal, CompanyInfoBox } from '../../../components/forms';
import { useCompanySelection } from '../../../hooks/useCompanySelection';
import { useCEPLookup } from '../../../hooks/useCEPLookup';
import ESocialReferenceAutocomplete from '../../companies/components/esocial/forms/ESocialReferenceAutocomplete';
import {
  CatFormData,
  CatReportType,
  AccidentType,
  AccidentLocationType,
  CatIssuer,
  Laterality,
  CAT_REPORT_TYPE_LABELS,
  ACCIDENT_TYPE_LABELS,
  ACCIDENT_LOCATION_TYPE_LABELS,
  CAT_ISSUER_LABELS,
  LATERALITY_LABELS
} from '../../../types/cat';

interface CatFormProps {
  formData: CatFormData;
  onChange: (data: Partial<CatFormData>) => void;
  errors?: Record<string, string>;
  readOnly?: boolean;
  // External data for selects
  companies?: Array<{ id: number; tradeName: string; legalName: string }>;
  employees?: Array<{ id: number; name: string; cpf: string }>;
  cidCodes?: Array<{ id: number; code: string; description: string }>;
  companyAddress?: {
    address?: string;
    addressNumber?: string;
    neighborhood?: string;
    zipCode?: string;
    city?: string;
    state?: string;
    ibgeCode?: string;
  };
  onCompanyChange?: (companyId: number) => void;
  onStepChange?: (step: number, isLastStep: boolean) => void;
}

const BRAZILIAN_STATES = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const STEPS = [
  'Funcionário e Acidente',
  'Local do Acidente',
  'Lesão e Diagnóstico',
  'Atendimento e Emissão',
  'Complementos'
];

// Fields required for each step validation
const STEP_FIELDS: Record<number, (keyof CatFormData)[]> = {
  0: ['companyId', 'employeeId', 'reportType', 'accidentType', 'issuer', 'accidentDate', 'generatingSituation'],
  1: ['accidentLocationType'],
  2: ['codParteAting', 'codNatLesao', 'codAgntCausador'],
  3: [],
  4: []
};

export const CatForm: React.FC<CatFormProps> = ({
  formData,
  onChange,
  errors = {},
  readOnly = false,
  companies: _companies = [],
  employees = [],
  cidCodes = [],
  companyAddress,
  onCompanyChange,
  onStepChange
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const { loading: cepLoading, lookupCEP } = useCEPLookup();
  const {
    modalOpen,
    selectedCompany,
    showInfoBox,
    openModal,
    handleSelect: handleCompanySelect,
  } = useCompanySelection({ isEditMode: readOnly, currentCompanyId: String(formData.companyId ?? '') });

  // Auto-fill company when selection changes
  useEffect(() => {
    if (selectedCompany && selectedCompany.id) {
      const currentValue = String(formData.companyId || '');
      if (currentValue !== selectedCompany.id) {
        onChange({ companyId: Number(selectedCompany.id) });
        if (onCompanyChange) {
          onCompanyChange(Number(selectedCompany.id));
        }
      }
    }
  }, [selectedCompany, formData.companyId, onChange, onCompanyChange]);

  const handleChange = (field: keyof CatFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string | number>
  ) => {
    const value = event.target.value;
    onChange({ [field]: value });
  };

  const handleSwitchChange = (field: keyof CatFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({ [field]: event.target.checked });
  };

  const handleEmployeeSelect = (_event: React.SyntheticEvent, value: { id: number; name: string; cpf: string } | null) => {
    onChange({ employeeId: value?.id || '' });
  };

  const handleCidCodeSelect = (_event: React.SyntheticEvent, value: { id: number; code: string; description: string } | null) => {
    onChange({ cidCodeId: value?.id || '' });
  };

  const handleLocationTypeChange = (event: SelectChangeEvent<string | number>) => {
    const value = event.target.value as AccidentLocationType;

    if (value === AccidentLocationType.EMPLOYER_ESTABLISHMENT && companyAddress) {
      onChange({
        accidentLocationType: value,
        streetName: companyAddress.address ?? '',
        streetNumber: companyAddress.addressNumber ?? '',
        neighborhood: companyAddress.neighborhood ?? '',
        zipCode: companyAddress.zipCode ?? '',
        cityCode: companyAddress.ibgeCode ?? '',
        state: companyAddress.state ?? '',
      });
    } else {
      onChange({
        accidentLocationType: value,
        streetType: '',
        streetName: '',
        streetNumber: '',
        complement: '',
        neighborhood: '',
        zipCode: '',
        cityCode: '',
        state: '',
      });
    }
  };

  const handleCEPLookup = async () => {
    const cep = formData.zipCode ?? '';
    if (cep.length !== 8) return;

    const data = await lookupCEP(cep);
    if (data) {
      onChange({
        streetName: data.address || formData.streetName,
        neighborhood: data.neighborhood || formData.neighborhood,
        cityCode: data.ibgeCode || formData.cityCode,
        state: data.state || formData.state,
      });
    }
  };

  const isEmployerEstablishment = formData.accidentLocationType === AccidentLocationType.EMPLOYER_ESTABLISHMENT;

  const selectedEmployee = employees.find(e => e.id === formData.employeeId);
  const selectedCidCode = cidCodes.find(c => c.id === formData.cidCodeId);

  const hasStepErrors = (step: number): boolean => {
    const fields = STEP_FIELDS[step] ?? [];
    return fields.some(field => !!errors[field]);
  };

  const handleNext = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (activeStep < STEPS.length - 1) {
      const nextStep = activeStep + 1;
      setActiveStep(nextStep);
      onStepChange?.(nextStep, nextStep === STEPS.length - 1);
    }
  };

  const handleBack = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (activeStep > 0) {
      const prevStep = activeStep - 1;
      setActiveStep(prevStep);
      onStepChange?.(prevStep, false);
    }
  };

  const handleStepClick = (step: number) => {
    if (readOnly || step <= activeStep) {
      setActiveStep(step);
      onStepChange?.(step, step === STEPS.length - 1);
    }
  };

  // Step 0: Funcionario e Acidente
  const renderEmployeeAndAccident = () => (
    <>
      {/* Employee Data */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Dados do Funcionário
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Autocomplete
            options={employees}
            getOptionLabel={(option) => `${option.name} (CPF: ${option.cpf})`}
            value={selectedEmployee || null}
            onChange={handleEmployeeSelect}
            disabled={readOnly || !formData.companyId}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Funcionário *"
                error={!!errors.employeeId}
                helperText={errors.employeeId || (!formData.companyId ? 'Selecione uma empresa primeiro' : '')}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Accident Data */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Dados do Acidente
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl fullWidth error={!!errors.reportType}>
            <InputLabel>Tipo de CAT *</InputLabel>
            <Select
              value={formData.reportType}
              onChange={handleChange('reportType')}
              label="Tipo de CAT *"
              disabled={readOnly}
            >
              {Object.values(CatReportType).map((type) => (
                <MenuItem key={type} value={type}>
                  {CAT_REPORT_TYPE_LABELS[type]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl fullWidth error={!!errors.accidentType}>
            <InputLabel>Tipo de Acidente *</InputLabel>
            <Select
              value={formData.accidentType}
              onChange={handleChange('accidentType')}
              label="Tipo de Acidente *"
              disabled={readOnly}
            >
              {Object.values(AccidentType).map((type) => (
                <MenuItem key={type} value={type}>
                  {ACCIDENT_TYPE_LABELS[type]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl fullWidth error={!!errors.issuer}>
            <InputLabel>Emissor *</InputLabel>
            <Select
              value={formData.issuer}
              onChange={handleChange('issuer')}
              label="Emissor *"
              disabled={readOnly}
            >
              {Object.values(CatIssuer).map((issuer) => (
                <MenuItem key={issuer} value={issuer}>
                  {CAT_ISSUER_LABELS[issuer]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            type="date"
            label="Data do Acidente *"
            value={formData.accidentDate}
            onChange={handleChange('accidentDate')}
            InputLabelProps={{ shrink: true }}
            error={!!errors.accidentDate}
            helperText={errors.accidentDate}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            type="time"
            label="Hora do Acidente"
            value={formData.accidentTime ?? ''}
            onChange={handleChange('accidentTime')}
            InputLabelProps={{ shrink: true }}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            label="Horas Trabalhadas Antes"
            value={formData.hoursWorkedBefore ?? ''}
            onChange={handleChange('hoursWorkedBefore')}
            placeholder="Ex: 04:30"
            disabled={readOnly}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Situação Geradora *"
            value={formData.generatingSituation}
            onChange={handleChange('generatingSituation')}
            error={!!errors.generatingSituation}
            helperText={errors.generatingSituation}
            placeholder="Descreva a situação que gerou o acidente..."
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <ESocialReferenceAutocomplete
            tableNumber={15}
            value={formData.codSitGeradora ?? ''}
            onChange={(code) => onChange({ codSitGeradora: code || undefined })}
            label="Situação Geradora - eSocial (Tabela 15)"
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl fullWidth>
            <InputLabel>Óbito</InputLabel>
            <Select
              value={formData.isDeathReport}
              onChange={handleChange('isDeathReport')}
              label="Óbito"
              disabled={readOnly}
            >
              <MenuItem value="N">Não</MenuItem>
              <MenuItem value="S">Sim</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </>
  );

  // Step 1: Local do Acidente
  const renderAccidentLocation = () => (
    <>
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Local do Acidente
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl fullWidth error={!!errors.accidentLocationType}>
            <InputLabel>Tipo de Local *</InputLabel>
            <Select
              value={formData.accidentLocationType}
              onChange={handleLocationTypeChange}
              label="Tipo de Local *"
              disabled={readOnly}
            >
              {Object.values(AccidentLocationType).map((type) => (
                <MenuItem key={type} value={type}>
                  {ACCIDENT_LOCATION_TYPE_LABELS[type]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            label="Tipo de Logradouro"
            value={formData.streetType ?? ''}
            onChange={handleChange('streetType')}
            placeholder="Ex: Rua, Avenida"
            disabled={readOnly || isEmployerEstablishment}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            label="Logradouro"
            value={formData.streetName ?? ''}
            onChange={handleChange('streetName')}
            disabled={readOnly || isEmployerEstablishment}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <TextField
            fullWidth
            label="Número"
            value={formData.streetNumber ?? ''}
            onChange={handleChange('streetNumber')}
            disabled={readOnly || isEmployerEstablishment}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            label="Complemento"
            value={formData.complement ?? ''}
            onChange={handleChange('complement')}
            disabled={readOnly || isEmployerEstablishment}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3
          }}>
          <TextField
            fullWidth
            label="Bairro"
            value={formData.neighborhood ?? ''}
            onChange={handleChange('neighborhood')}
            disabled={readOnly || isEmployerEstablishment}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3
          }}>
          <TextField
            fullWidth
            label="CEP"
            value={formData.zipCode ?? ''}
            onChange={handleChange('zipCode')}
            placeholder="00000000"
            inputProps={{ maxLength: 8 }}
            disabled={readOnly || isEmployerEstablishment}
            InputProps={{
              endAdornment: !readOnly && !isEmployerEstablishment ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleCEPLookup}
                    disabled={cepLoading || (formData.zipCode ?? '').length !== 8}
                    size="small"
                  >
                    {cepLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3
          }}>
          <TextField
            fullWidth
            label="Código IBGE"
            value={formData.cityCode ?? ''}
            onChange={handleChange('cityCode')}
            disabled={readOnly || isEmployerEstablishment}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <FormControl fullWidth>
            <InputLabel>UF</InputLabel>
            <Select
              value={formData.state ?? ''}
              onChange={handleChange('state')}
              label="UF"
              disabled={readOnly || isEmployerEstablishment}
            >
              <MenuItem value="">-</MenuItem>
              {BRAZILIAN_STATES.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </>
  );

  // Step 2: Lesao e Diagnostico
  const renderInjuryAndDiagnosis = () => (
    <>
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Lesão e Diagnóstico
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <ESocialReferenceAutocomplete
            tableNumber={13}
            value={formData.codParteAting ?? ''}
            onChange={(code) => onChange({ codParteAting: code || undefined })}
            label="Parte do Corpo Atingida - eSocial (Tabela 13) *"
            required
            error={!!errors.affectedBodyPart && !formData.codParteAting}
            helperText={errors.affectedBodyPart}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl fullWidth>
            <InputLabel>Lateralidade</InputLabel>
            <Select
              value={formData.laterality ?? ''}
              onChange={handleChange('laterality')}
              label="Lateralidade"
              disabled={readOnly}
            >
              <MenuItem value="">-</MenuItem>
              {Object.values(Laterality).map((lat) => (
                <MenuItem key={lat} value={lat}>
                  {LATERALITY_LABELS[lat]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <ESocialReferenceAutocomplete
            tableNumber={17}
            value={formData.codNatLesao ?? ''}
            onChange={(code) => onChange({ codNatLesao: code || undefined })}
            label="Natureza da Lesão - eSocial (Tabela 17) *"
            required
            error={!!errors.injuryNature && !formData.codNatLesao}
            helperText={errors.injuryNature}
            disabled={readOnly}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Descrição da Lesão"
            value={formData.injuryDescription ?? ''}
            onChange={handleChange('injuryDescription')}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <ESocialReferenceAutocomplete
            tableNumber={14}
            value={formData.codAgntCausador ?? ''}
            onChange={(code) => onChange({ codAgntCausador: code || undefined })}
            label="Agente Causador - eSocial (Tabela 14) *"
            required
            error={!!errors.causingAgent && !formData.codAgntCausador}
            helperText={errors.causingAgent}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            fullWidth
            label="Diagnóstico Provável"
            value={formData.probableDiagnosis ?? ''}
            onChange={handleChange('probableDiagnosis')}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Autocomplete
            options={cidCodes}
            getOptionLabel={(option) => `${option.code} - ${option.description}`}
            value={selectedCidCode || null}
            onChange={handleCidCodeSelect}
            disabled={readOnly}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Código CID-10"
              />
            )}
          />
        </Grid>
      </Grid>
    </>
  );

  // Step 3: Atendimento e Emissao
  const renderTreatmentAndIssuer = () => (
    <>
      {/* Medical Treatment */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Atendimento Médico
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 3
          }}>
          <TextField
            fullWidth
            type="date"
            label="Data do Atendimento"
            value={formData.treatmentDate ?? ''}
            onChange={handleChange('treatmentDate')}
            InputLabelProps={{ shrink: true }}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3
          }}>
          <TextField
            fullWidth
            type="time"
            label="Hora do Atendimento"
            value={formData.treatmentTime ?? ''}
            onChange={handleChange('treatmentTime')}
            InputLabelProps={{ shrink: true }}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <FormControl fullWidth>
            <InputLabel>Internação</InputLabel>
            <Select
              value={formData.wasHospitalized ?? 'N'}
              onChange={handleChange('wasHospitalized')}
              label="Internação"
              disabled={readOnly}
            >
              <MenuItem value="N">Não</MenuItem>
              <MenuItem value="S">Sim</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <TextField
            fullWidth
            type="number"
            label="Duração (dias)"
            value={formData.treatmentDuration ?? ''}
            onChange={handleChange('treatmentDuration')}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <FormControl fullWidth>
            <InputLabel>Afastamento</InputLabel>
            <Select
              value={formData.willBeAbsent ?? 'N'}
              onChange={handleChange('willBeAbsent')}
              label="Afastamento"
              disabled={readOnly}
            >
              <MenuItem value="N">Não</MenuItem>
              <MenuItem value="S">Sim</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Issuer Professional */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Profissional Emissor
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            fullWidth
            label="Nome do Profissional"
            value={formData.issuerName ?? ''}
            onChange={handleChange('issuerName')}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <TextField
            fullWidth
            label="Órgão de Classe"
            value={formData.classEntity ?? ''}
            onChange={handleChange('classEntity')}
            placeholder="Ex: CRM"
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <TextField
            fullWidth
            label="Número"
            value={formData.classEntityNumber ?? ''}
            onChange={handleChange('classEntityNumber')}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <FormControl fullWidth>
            <InputLabel>UF</InputLabel>
            <Select
              value={formData.classEntityState ?? ''}
              onChange={handleChange('classEntityState')}
              label="UF"
              disabled={readOnly}
            >
              <MenuItem value="">-</MenuItem>
              {BRAZILIAN_STATES.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </>
  );

  // Step 4: Complementos (eSocial, Notes)
  const renderComplements = () => (
    <>
      {/* eSocial */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Integração eSocial
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.sendToEsocial}
                onChange={handleSwitchChange('sendToEsocial')}
                disabled={readOnly}
              />
            }
            label="Enviar para eSocial"
          />
        </Grid>
        {/* Ambiente eSocial é determinado automaticamente pelo NODE_ENV do servidor */}
      </Grid>

      {/* Notes */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Observações
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Observações"
            value={formData.notes ?? ''}
            onChange={handleChange('notes')}
            placeholder="Observações adicionais..."
            disabled={readOnly}
          />
        </Grid>
      </Grid>
    </>
  );

  const isLastStep = activeStep === STEPS.length - 1;
  const isFirstStep = activeStep === 0;

  return (
    <Box>
      {showInfoBox && selectedCompany && (
        <CompanyInfoBox
          companyName={selectedCompany.name}
          onEdit={openModal}
        />
      )}

      {/* Stepper */}
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {STEPS.map((label, index) => (
          <Step
            key={label}
            completed={index < activeStep}
            sx={{ cursor: readOnly || index <= activeStep ? 'pointer' : 'default' }}
            onClick={() => handleStepClick(index)}
          >
            <StepLabel error={hasStepErrors(index)}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step Content */}
      <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
        {renderEmployeeAndAccident()}
      </Box>
      <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
        {renderAccidentLocation()}
      </Box>
      <Box sx={{ display: activeStep === 2 ? 'block' : 'none' }}>
        {renderInjuryAndDiagnosis()}
      </Box>
      <Box sx={{ display: activeStep === 3 ? 'block' : 'none' }}>
        {renderTreatmentAndIssuer()}
      </Box>
      <Box sx={{ display: activeStep === 4 ? 'block' : 'none' }}>
        {renderComplements()}
      </Box>

      {/* Navigation Buttons */}
      {!readOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={handleBack}
            disabled={isFirstStep}
          >
            Anterior
          </Button>
          <Button
            variant="contained"
            endIcon={!isLastStep ? <NextIcon /> : undefined}
            onClick={handleNext}
            disabled={isLastStep}
          >
            {isLastStep ? 'Último passo' : 'Próximo'}
          </Button>
        </Box>
      )}

      <CompanySelectionModal
        open={modalOpen}
        onSelect={handleCompanySelect}
      />
    </Box>
  );
};

export default CatForm;
